import React, { useState } from 'react'
import GenericButton from '../../../components/GenericButton'
import { ButtonType } from '../../../components/GenericButton/styles'
import { ReactComponent as ExportIcon } from '../../../assets/Export-icon.svg'
import { DistributionManager } from '../../../APIManager'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from '../../../components/Notifications'
import BulkExportModal, { buildExportConfigParams, INSTRUCTOR_BULK_EXPORT_MODAL_PROPS, type ExportConfig } from '../../ReportsFeatureV2/components/BulkExportModal'
import { useTaskStatus } from '../../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../../components/ExportBackgroundTaskRunningModal'

interface ExportProps {
  disabled?: boolean
}

export const Export: React.FC<ExportProps> = ({ disabled }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [searchParams] = useSearchParams()
  const { id: instructorId } = useParams()
  const termId = searchParams.get('termId') ?? ''
  const { taskId, setTaskId, showBackgroundModal, setShowBackgroundModal, checkStatusBeforeExport } = useTaskStatus('exportTask_instructorReport')

  const downloadFile = (data: any, fileType: string): void => {
    const contentDisposition = data?.headers?.['content-disposition'] ?? data?.headers?.contentDisposition
    const fileName = contentDisposition?.match(/filename="?(.*?)"?$/)?.[1] ?? 'Export.zip'
    const mimeType = fileType === 'CSV' ? 'text/csv' : 'application/zip'
    const blob = new Blob([data?.data], { type: mimeType })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = fileName
    link.click()
    URL.revokeObjectURL(url)
    notification('File successfully downloaded', { variant: 'success', subTitle: fileName })
  }

  const handleExport = async (config: ExportConfig): Promise<void> => {
    const allowed = await checkStatusBeforeExport()
    if (!allowed) return

    setIsExporting(true)
    try {
      const exportConfigParams = buildExportConfigParams(config)
      const exportByParam = exportConfigParams.export_by
      const mergePdfParam = exportConfigParams.merge_pdf === 'true'
      let result
      if (config.fileType === 'PDF') {
        result = instructorId
          ? await DistributionManager.exportPDFByInstructorId(instructorId, termId, exportByParam, mergePdfParam)
          : await DistributionManager.exportPdf(termId, exportByParam, mergePdfParam)
      } else {
        result = instructorId
          ? await DistributionManager.exportCSVByInstructorId(instructorId, termId, exportByParam)
          : await DistributionManager.exportCSV(termId, exportByParam)
      }

      if (result.status === 202) {
        // Camelized by the shared api client's axios-case-converter middleware. Not currently
        // reachable in practice — the instructor PDF/CSV export endpoints this calls never
        // return 202 today — but kept correct (not the backend's raw task_id) in case that
        // changes.
        if (result.data?.taskId) {
          setTaskId(result.data.taskId)
          setShowBackgroundModal(true)
        } else {
          notification(result.data?.message ?? 'Your export is running in the background.', { variant: 'info' })
        }
        setIsModalOpen(false)
        return
      }

      downloadFile(result, config.fileType)
      setIsModalOpen(false)
    } catch {
      notification('Something went wrong while downloading the file', { variant: 'error' })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <ExportBackgroundTaskRunningModal
        isOpen={showBackgroundModal}
        onClose={() => { setShowBackgroundModal(false) }}
        taskId={taskId ?? ''}
      />
      <GenericButton
        buttonType={ButtonType.Secondary}
        label="Export"
        disabled={disabled}
        endIcon={<ExportIcon style={{ opacity: disabled ? 0.2 : 1, pointerEvents: 'none' }} />}
        onClick={() => { setIsModalOpen(true) }}
      />

      <BulkExportModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false) }}
        onExport={(config) => { void handleExport(config) }}
        isExporting={isExporting}
        {...INSTRUCTOR_BULK_EXPORT_MODAL_PROPS}
      />
    </>
  )
}
